<template>
  <div class="phone-input-wrap">
    <select
      v-model="selectedCountryCode"
      class="phone-country-select"
      aria-label="Código do país"
    >
      <option
        v-for="country in countryList"
        :key="country.code"
        :value="country.code"
      >
        {{ country.dial }}
      </option>
    </select>
    <input
      ref="inputRef"
      :id="id"
      type="text"
      inputmode="numeric"
      :value="displayValue"
      :placeholder="nationalPlaceholder"
      :aria-label="ariaLabel"
      :aria-invalid="invalid"
      :aria-describedby="ariaDescribedby"
      class="phone-input"
      @input="onInput"
      @blur="onBlur"
      @focus="onFocus"
    />
  </div>
</template>

<script>
import {
  COUNTRY_LIST,
  getCountryCodeFromRaw,
  formatPhoneBr,
  validatePhone,
} from '@/utils/phoneUtils';

export default {
  name: 'PhoneInput',
  props: {
    modelValue: {
      type: String,
      default: null,
    },
    id: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    ariaLabel: { type: String, default: '' },
    ariaDescribedby: { type: String, default: '' },
    invalid: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      focused: false,
      selectedCountryCode: '55',
      /** While focused: national digits only (no country code). */
      inputStr: '',
    };
  },
  computed: {
    countryList() {
      return COUNTRY_LIST;
    },
    maxNationalLength() {
      return this.selectedCountryCode === '55' ? 11 : 12;
    },
    nationalPlaceholder() {
      return this.placeholder || (this.selectedCountryCode === '55' ? '(00) 00000-0000' : '000 000 000');
    },
    displayValue() {
      if (this.focused) {
        return this.formatNationalInput(this.inputStr, this.selectedCountryCode);
      }
      const raw = this.modelValue;
      if (!raw) return '';
      const parsed = getCountryCodeFromRaw(raw);
      if (!parsed) return '';
      if (parsed.code === '55') return formatPhoneBr(raw);
      return this.groupNationalDigits(parsed.nationalDigits);
    },
  },
  watch: {
    modelValue: {
      handler(val) {
        if (val && !this.focused) {
          const parsed = getCountryCodeFromRaw(val);
          if (parsed) this.selectedCountryCode = parsed.code;
        }
      },
      immediate: true,
    },
  },
  methods: {
    formatNationalInput(digits, code) {
      const d = (digits || '').replace(/\D/g, '').slice(0, this.maxNationalLength);
      if (code === '55') {
        if (!d) return '';
        if (d.length <= 2) return `(${d}`;
        if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
        return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
      }
      return this.groupNationalDigits(d);
    },
    groupNationalDigits(digits) {
      if (!digits) return '';
      return digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
    },
    onFocus() {
      this.focused = true;
      const raw = this.modelValue;
      const parsed = getCountryCodeFromRaw(raw);
      if (parsed && parsed.code === this.selectedCountryCode) {
        this.inputStr = parsed.nationalDigits;
      } else if (raw && typeof raw === 'string') {
        const digits = raw.replace(/\D/g, '');
        if (digits.startsWith(this.selectedCountryCode)) {
          this.inputStr = digits.slice(this.selectedCountryCode.length).slice(0, this.maxNationalLength);
        } else {
          this.inputStr = digits.slice(0, this.maxNationalLength);
        }
      } else {
        this.inputStr = '';
      }
    },
    onBlur() {
      this.focused = false;
      const digits = this.inputStr.replace(/\D/g, '').slice(0, this.maxNationalLength);
      this.inputStr = '';
      const minLen = this.selectedCountryCode === '55' ? 10 : 8;
      if (digits.length < minLen) {
        this.$emit('update:modelValue', null);
        return;
      }
      const raw = this.selectedCountryCode + digits;
      const { valid } = validatePhone(raw);
      this.$emit('update:modelValue', valid ? raw : null);
    },
    onInput(e) {
      this.inputStr = e.target.value.replace(/\D/g, '').slice(0, this.maxNationalLength);
    },
  },
};
</script>

<style scoped>
.phone-input-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.phone-input-wrap:focus-within {
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 3px var(--color-accent-soft);
}

.phone-country-select {
  padding: 10px 8px 10px 12px;
  margin: 0;
  font-family: inherit;
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  font-weight: 500;
  background: transparent;
  border: none;
  border-right: 1px solid var(--color-border);
  border-radius: var(--radius-sm) 0 0 var(--radius-sm);
  cursor: pointer;
  min-width: 0;
}

.phone-country-select:focus {
  outline: none;
}

.phone-input {
  flex: 1;
  min-width: 0;
  border: none;
  padding: 10px 12px 10px 10px;
  font-family: inherit;
  font-size: 0.95rem;
  color: var(--color-text);
  background: transparent;
}

.phone-input:focus {
  outline: none;
  box-shadow: none;
}

.phone-input::placeholder {
  color: var(--color-text-muted);
}
</style>
